// Design frames: wireframes/18-deals-m3.html (18.1 list, 18.2 new deal).
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMatch, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  BottomSheet,
  Button,
  Card,
  Checkbox,
  Chip,
  Fab,
  List,
  ListItem,
  Notice,
  SelectField,
  Skeleton,
  StateScreen,
  Tag,
  TextField,
  iconAdd,
} from '@sauda/ui/kit';
import { Screen, useChrome } from '../app/chrome.js';
import { useAuth } from '../auth/AuthContext.js';
import { ApiError, api } from '../lib/api.js';
import { nextStepLabel } from '../lib/next-step.js';
import type { DealSummary } from '../lib/types.js';
import { FINISHED } from './deals/stages.js';

/** Starts a deal without a visit. Such a deal has no introduction record, and the sheet says so. */
function NewDealSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const cache = useQueryClient();
  const [propertyId, setPropertyId] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const properties = useQuery({
    queryKey: ['property-options', auth.agencyId],
    queryFn: () => api<{ id: string; title: string }[]>('/properties'),
    enabled: open,
    // A property created a moment ago has to be in the list, whatever the cache says.
    staleTime: 0,
  });
  const create = useMutation({
    mutationFn: () =>
      api<{ id?: string; dealId?: string }>('/deals', {
        method: 'POST',
        body: {
          propertyId,
          manualClient: { name: name.trim(), phone: phone.trim(), consentAttested: true },
        },
      }),
    onSuccess: async (deal) => {
      await cache.invalidateQueries({ queryKey: ['deals'] });
      onClose();
      void navigate(`/deals/${deal.id ?? deal.dealId ?? ''}`);
    },
    onError: (e) =>
      setError(
        e instanceof ApiError && e.status < 500 && e.message !== ''
          ? e.message
          : t('deals.createFailed'),
      ),
  });
  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      label={t('deals.manualTitle')}
      title={t('deals.manualTitle')}
      closeLabel={t('app.close')}
      actions={
        <Button
          size="lg"
          block
          loading={create.isPending}
          disabled={!consent || propertyId === '' || name.trim().length < 2 || phone.trim() === ''}
          onClick={() => create.mutate()}
        >
          {t('deals.create')}
        </Button>
      }
    >
      <div className="k-stack">
        <Notice tone="info">{t('deals.manualHelp')}</Notice>
        <SelectField
          label={t('deals.property')}
          value={propertyId}
          onChange={(e) => setPropertyId(e.target.value)}
          placeholder={t('deals.chooseProperty')}
          options={(properties.data ?? []).map((p) => ({ value: p.id, label: p.title }))}
        />
        <TextField
          label={t('deals.clientName')}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          label={t('deals.clientPhone')}
          type="tel"
          inputMode="numeric"
          prefix="+91"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <Checkbox
          label={t('deals.consent')}
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
        />
        {error === null ? null : <Notice tone="error">{error}</Notice>}
      </div>
    </BottomSheet>
  );
}

type Filter = 'needs' | 'memo' | 'agreement' | 'registration' | 'closed';
const FILTERS: Filter[] = ['needs', 'memo', 'agreement', 'registration', 'closed'];

/** Which chips a deal answers to. */
function inFilter(d: DealSummary, filter: Filter): boolean {
  switch (filter) {
    case 'needs':
      return (
        !FINISHED.includes(d.state) && d.nextStep !== null && d.nextStep !== 'collect_signatures'
      );
    case 'memo':
      return d.state === 'token_recorded' && d.memoSigners > 0;
    case 'agreement':
      return d.state === 'memo_signed' || d.state === 'agreement_drafted';
    case 'registration':
      return d.state === 'registered';
    case 'closed':
      return FINISHED.includes(d.state);
  }
}

/** The deals list: each row shows where the deal stands and its next step. A filter keeps finished deals out of the way. */
export function DealsScreen() {
  const { t } = useTranslation();
  const auth = useAuth();
  const chrome = useChrome();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const current = useMatch('/deals/:id/*')?.params['id'];
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<Filter>('needs');
  const deals = useQuery({
    queryKey: ['deals', auth.agencyId],
    queryFn: () => api<DealSummary[]>('/deals'),
  });

  // Today's "New deal" arrives as ?new=1: open the sheet once, then clean the address.
  useEffect(() => {
    if (params.get('new') === '1') {
      setOpen(true);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  const all = deals.data ?? [];
  const shown = all.filter((d) => inFilter(d, filter));
  return (
    <Screen title={t('nav.deals')} large>
      {deals.isPending ? <Skeleton height={72} /> : null}
      {deals.isError ? (
        <Notice
          tone="error"
          action={
            <Button variant="text" onClick={() => void deals.refetch()}>
              {t('app.retry')}
            </Button>
          }
        >
          {t('today.loadFailed')}
        </Notice>
      ) : null}
      <div className="chips-row" role="group" aria-label={t('deals.filter')}>
        {FILTERS.map((f) => (
          <Chip key={f} selected={filter === f} onClick={() => setFilter(f)}>
            {t(`deals.filter_${f}`)}
          </Chip>
        ))}
      </div>
      {deals.isSuccess && shown.length === 0 ? (
        filter === 'needs' ? (
          <StateScreen
            kind="empty"
            compact
            title={t('deals.none')}
            action={
              chrome.readOnly ? undefined : (
                <Button onClick={() => setOpen(true)}>{t('deals.newManual')}</Button>
              )
            }
          />
        ) : (
          <Card>
            <p className="k-t-body k-muted">{t('deals.noneFinished')}</p>
          </Card>
        )
      ) : null}
      {shown.length === 0 ? null : (
        <List label={t('nav.deals')}>
          {shown.map((d) => {
            const label =
              d.nextStep === null
                ? null
                : nextStepLabel(d.nextStep, { signed: d.memoSigned, total: d.memoSigners || 3 });
            const waiting = d.nextStep === 'collect_signatures';
            const stateText =
              d.state === 'token_recorded' && d.memoSigners > 0
                ? t('deals.signedOf', { signed: d.memoSigned, total: d.memoSigners })
                : t(`deals.state_${d.state}`);
            return (
              <ListItem
                key={d.id}
                selected={current === d.id}
                title={d.propertyTitle}
                supporting={[d.clientName, stateText].filter(Boolean).join(' · ')}
                tags={d.hasIntroduction ? undefined : <Tag>{t('deals.noIntroduction')}</Tag>}
                onClick={() => void navigate(`/deals/${d.id}`)}
                trailing={waiting ? <Tag>{t('deals.waiting')}</Tag> : undefined}
                action={
                  label === null || waiting || FINISHED.includes(d.state) ? undefined : (
                    <Button variant="tonal" onClick={() => void navigate(`/deals/${d.id}`)}>
                      {t(label.key, label.values)}
                    </Button>
                  )
                }
              />
            );
          })}
        </List>
      )}
      {chrome.readOnly ? null : (
        <div className="fab-slot">
          <Fab icon={iconAdd} label={t('deals.newShort')} extended onClick={() => setOpen(true)} />
        </div>
      )}
      <NewDealSheet open={open} onClose={() => setOpen(false)} />
    </Screen>
  );
}
